import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ENV_CONFIG } from '@ps/core-config';
import { withLoading } from '@ps/core-overlay';
import { SupplierBlendDto } from './dtos/supplier-blend.dto';
import { SupplierBlendMapper } from './mappers/supplier-blend.mapper';
import { SupplierBlendPayloadMapper } from './mappers/supplier-blend-payload.mapper';
import {
  SupplierBlend,
  SupplierBlendSave,
} from '../models/supplier-blend.model';

@Injectable({ providedIn: 'root' })
export class SupplierBlendApi {
  private readonly _http = inject(HttpClient);
  private readonly _envConfig = inject(ENV_CONFIG);
  private readonly _mapper = new SupplierBlendMapper();
  private readonly _payloadMapper = new SupplierBlendPayloadMapper();

  private readonly _baseUrl = `${this._envConfig.pureSpectrum.url}/buyers/v2/supplier-blends`;

  public getAll(): Observable<SupplierBlend[]> {
    return this._http
      .get<SupplierBlendDto[]>(this._baseUrl, {
        context: withLoading('page'),
      })
      .pipe(map((dtos) => this._mapper.fromDtoList(dtos)));
  }

  public create(data: SupplierBlendSave): Observable<SupplierBlend> {
    const payload = this._payloadMapper.toPayload(data);
    return this._http
      .post<SupplierBlendDto>(this._baseUrl, payload, {
        context: withLoading('page'),
      })
      .pipe(map((dto) => this._mapper.fromDto(dto)));
  }

  public update(
    id: string,
    data: SupplierBlendSave,
  ): Observable<SupplierBlend> {
    const payload = this._payloadMapper.toPayload(data);
    return this._http
      .put<SupplierBlendDto>(`${this._baseUrl}/${id}`, payload, {
        context: withLoading('page'),
      })
      .pipe(map((dto) => this._mapper.fromDto(dto)));
  }

  public delete(id: string): Observable<void> {
    return this._http
      .delete(`${this._baseUrl}/${id}`, {
        context: withLoading('page'),
      })
      .pipe(map(() => undefined));
  }

  public setDefault(id: string, isDefault: boolean): Observable<void> {
    return this._http
      .patch(`${this._baseUrl}/set-default/${id}/${isDefault}`, '', {
        context: withLoading('page'),
      })
      .pipe(map(() => undefined));
  }

  public lockAll(blendIds: string[], isLocked: boolean): Observable<void> {
    return this._http
      .patch(
        `${this._baseUrl}/set-locked`,
        { blendIds, isLocked },
        { context: withLoading('page') },
      )
      .pipe(map(() => undefined));
  }
}
